import type { ComponentType } from 'react';

import { cn } from '../../../shared/lib/cn';
import {
  CheckIcon,
  ClockIcon,
  CloseIcon,
  StopIcon,
  type TIconProps,
} from '../../../shared/ui/icons';
import { StatusDisc } from '../../../shared/ui/status-disc';
import type { IClientStatus, TClientShape } from '../lib';

const GLYPH: Record<TClientShape, ComponentType<TIconProps>> = {
  check: CheckIcon,
  cross: CloseIcon,
  clock: ClockIcon,
  minus: StopIcon,
};

const WORD = (status: IClientStatus): string =>
  status.tone === 'failure'
    ? 'text-danger-text'
    : status.tone === 'suppressed'
      ? 'text-muted'
      : 'text-primary';

/** The list's cell: a disc and the short word, cut before it wraps. */
export const ClientStatusMark = ({ status }: { status: IClientStatus }) => (
  <span className="flex min-w-0 items-center gap-2 text-data">
    <StatusDisc tone={status.tone} icon={GLYPH[status.shape]} />
    <span className={cn('min-w-0 truncate', WORD(status))} title={status.label}>
      {status.label}
    </span>
  </span>
);

/** The page's banner under the header: the headline, then the one fact that explains it. */
export const ClientStatusBanner = ({ status }: { status: IClientStatus }) => (
  <div
    role="status"
    className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-control bg-surface-raised px-input py-inset"
  >
    <span className="flex min-w-0 items-center gap-2 text-data">
      <StatusDisc tone={status.tone} icon={GLYPH[status.shape]} />
      <span
        className={cn('min-w-0', status.tone === 'failure' ? 'text-danger-text' : 'text-primary')}
      >
        {status.headline}
      </span>
    </span>
    {status.detail && (
      <span className="min-w-0 font-mono text-micro text-faint">{status.detail}</span>
    )}
  </div>
);
